import { useEffect, type MouseEvent, type ReactElement } from 'react';
import { goHome } from '../router';
import { prefetchRouteData } from '../route-data';
import './PageHome.css';

/**
 * The Home control (docs/frontend-ui-standard.md §3): the one-tap exit from a
 * deep trail, mounted on every level 3-4 page, the documented cold-arrival
 * pages (theme landing, Result), and the sign-in flow — always in the toolbar
 * row's right-hand action cluster, whose anatomy and ink it shares with
 * ShareButton. Level 1-2 pages never mount it: their ladder Back already
 * reaches home in one tap.
 *
 * Clicking collapses to the home entry already in the visitor's trail (the
 * stack shrinks) or pushes home when this trip never went there (router.ts,
 * goHome). The href is a real path so middle-click, cmd-click, and copy-link
 * behave like a link.
 */
export function PageHome({ className = '', onIntercept }: {
  className?: string;
  /** Return true to hold the trip (a dialog takes this click) — same contract as PageBack. */
  onIntercept?: () => boolean;
}): ReactElement {
  useEffect(() => prefetchRouteData('/'), []);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (onIntercept?.()) return;
    goHome();
  };

  return (
    <a
      href="/"
      className={className ? `ember-page-home ${className}` : 'ember-page-home'}
      aria-label="Home"
      data-log="page_home"
      onClick={handleClick}
      onFocus={() => prefetchRouteData('/')}
      onPointerEnter={() => prefetchRouteData('/')}
      onTouchStart={() => prefetchRouteData('/')}
    >
      <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="m4 11.2 8-7.2 8 7.2" />
        <path d="M6.2 9.9v8.9a1.7 1.7 0 0 0 1.7 1.7h8.2a1.7 1.7 0 0 0 1.7-1.7V9.9" />
      </svg>
      <span>Home</span>
    </a>
  );
}
